import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { apiClient } from '../utils/api';
import { useToast } from '../components/common/ToastProvider';
import ErrorMessage from '../components/common/ErrorMessage';
import '../styles/dashboard.css'; // Re-use dashboard styles for consistency

export const FCMManager = () => {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState('');
    const [tokens, setTokens] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [loadingTokens, setLoadingTokens] = useState(false);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedToken, setSelectedToken] = useState(null);
    const [messageTitle, setMessageTitle] = useState('');
    const [messageBody, setMessageBody] = useState('');
    const [sending, setSending] = useState(false);
    const [apiResponse, setApiResponse] = useState(null);

    const { addToast } = useToast();

    // Fetch users on mount
    useEffect(() => {
        const fetchUsers = async () => {
            setLoadingUsers(true);
            try {
                const response = await apiClient.get('users');
                // Handle response structure: { users: [...] } (parsed by api.js)
                const userList = response.users || [];
                setUsers(userList);
            } catch (err) {
                console.error('Failed to fetch users:', err);
                setError('Failed to load users.');
            } finally {
                setLoadingUsers(false);
            }
        };

        fetchUsers();
    }, []);

    // Fetch tokens when user is selected
    useEffect(() => {
        if (!selectedUser) {
            setTokens([]);
            return;
        }

        const fetchTokens = async () => {
            setLoadingTokens(true);
            setError(null);
            try {
                const response = await apiClient.get(`auth/users/${selectedUser}/fcm-tokens`);
                // Handle response structure: { status: 'success', data: [...] } -> parsed to [...]
                setTokens(response || []);
            } catch (err) {
                console.error('Failed to fetch tokens:', err);
                setError('Failed to load FCM tokens for the selected user.');
                setTokens([]);
            } finally {
                setLoadingTokens(false);
            }
        };

        fetchTokens();
    }, [selectedUser]);

    const handleUserChange = (e) => {
        setSelectedUser(e.target.value);
    };

    const handleOpenModal = (token) => {
        setSelectedToken(token);
        setMessageTitle('Test Notification');
        setMessageBody('This is a test message from the admin dashboard.');
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedToken(null);
        setSending(false);
        setApiResponse(null);
    };

    const handleSendNotification = async (e) => {
        e.preventDefault();
        if (!selectedToken || !messageTitle || !messageBody) {
            return;
        }

        setSending(true);
        setApiResponse(null);
        try {
            const response = await apiClient.post('notifications/send-to-device', {
                device_mac: selectedToken.device_mac,
                title: messageTitle,
                body: messageBody,
            });

            setApiResponse(response);

            addToast({
                type: 'success',
                title: 'Notification Sent',
                description: `Message sent to device ${selectedToken.device_mac}`,
            });
            // Don't close modal immediately so user can see response
        } catch (err) {
            console.error('Failed to send notification:', err);
            setApiResponse({ error: err.message || 'Failed to send notification' });
            addToast({
                type: 'error',
                title: 'Send Failed',
                description: err.message || 'Failed to send notification.',
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="dashboard-content">
            <header style={{ marginBottom: '20px' }}>
                <h2>FCM Token Manager</h2>
                <p className="section-intro">
                    View registered FCM tokens for users and send test notifications.
                </p>
            </header>

            {error && <ErrorMessage title="Error" message={error} />}

            <div className="data-controls">
                <div className="data-controls__layout">
                    <div className="device-dropdown">
                        <label htmlFor="user-select">USER:</label>
                        <select
                            id="user-select"
                            value={selectedUser}
                            onChange={handleUserChange}
                            disabled={loadingUsers}
                        >
                            <option value="">Select a User...</option>
                            {users.map((user) => (
                                <option key={user.id} value={user.id}>
                                    {user.firstname} {user.lastname} ({user.email})
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <hr />

            {loadingTokens && (
                <div className="loading-message">
                    <p>Loading tokens...</p>
                </div>
            )}

            {!loadingTokens && selectedUser && tokens.length === 0 && (
                <div className="empty-state">
                    <p>No FCM tokens found for this user.</p>
                </div>
            )}

            {!loadingTokens && tokens.length > 0 && (
                <div className="table-container">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Device MAC</th>
                                <th>FCM Token (Snippet)</th>
                                <th>SNS Endpoint ARN</th>
                                <th>Created At</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tokens.map((token, index) => (
                                <tr key={index}>
                                    <td>{token.device_mac}</td>
                                    <td title={token.fcm_token}>
                                        {token.fcm_token.substring(0, 20)}...
                                    </td>
                                    <td title={token.sns_endpoint_arn}>
                                        {token.sns_endpoint_arn
                                            ? token.sns_endpoint_arn.split(':').pop()
                                            : 'N/A'}
                                    </td>
                                    <td>{new Date(token.created_at).toLocaleString()}</td>
                                    <td>
                                        <button
                                            className="primary-btn small-btn"
                                            onClick={() => handleOpenModal(token)}
                                            disabled={!token.sns_endpoint_arn}
                                        >
                                            Send Test
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {isModalOpen && ReactDOM.createPortal(
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="modal__header">
                            <h3>Send Test Notification</h3>
                            <button className="modal__close" onClick={handleCloseModal}>&times;</button>
                        </div>
                        <div className="modal__body">
                            <form onSubmit={handleSendNotification} id="notification-form">
                                <div className="form-field">
                                    <label className="form-label">Device MAC</label>
                                    <input
                                        type="text"
                                        value={selectedToken?.device_mac || ''}
                                        disabled
                                    />
                                </div>
                                <div className="form-field">
                                    <label className="form-label">Title</label>
                                    <input
                                        type="text"
                                        value={messageTitle}
                                        onChange={(e) => setMessageTitle(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="form-field">
                                    <label className="form-label">Body</label>
                                    <textarea
                                        value={messageBody}
                                        onChange={(e) => setMessageBody(e.target.value)}
                                        required
                                        rows="3"
                                    />
                                </div>
                            </form>

                            {apiResponse && (
                                <div className="api-response-container">
                                    <h4>API Response</h4>
                                    <pre className="api-response-code">
                                        {JSON.stringify(apiResponse, null, 2)}
                                    </pre>
                                </div>
                            )}
                        </div>
                        <div className="modal__footer">
                            <button type="button" className="secondary-btn" onClick={handleCloseModal}>
                                Close
                            </button>
                            <button type="submit" form="notification-form" className="primary-btn" disabled={sending}>
                                {sending ? 'Sending...' : 'Send'}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};
